import axios from 'axios';
import { API_BASE_URL } from './config';

// Interfaces
export interface Role {
  _id: string;
  name: string;
  displayName: string;
  description: string;
  category: 'system' | 'project' | 'custom';
  level: number; // hierarchy level (1 = highest)
  isActive: boolean;
  isDefault: boolean;
  permissions: Permission[];
  users: {
    _id: string;
    name: string;
    email: string;
  }[];
  parentRole?: string;
  childRoles: string[];
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface Permission {
  _id: string;
  name: string;
  displayName: string;
  description: string;
  category: 'project' | 'user' | 'system' | 'data' | 'reporting';
  resource: string; // e.g., 'projects', 'users', 'documents'
  actions: PermissionAction[];
  conditions?: PermissionCondition[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PermissionAction {
  name: string; // e.g., 'create', 'read', 'update', 'delete'
  displayName: string;
  description: string;
}

export interface PermissionCondition {
  field: string;
  operator: 'equals' | 'not_equals' | 'in' | 'not_in' | 'contains';
  value: any;
  description: string;
}

export interface UserRole {
  _id: string;
  userId: string;
  userName: string;
  userEmail: string;
  roleId: string;
  roleName: string;
  assignedBy: {
    _id: string;
    name: string;
    email: string;
  };
  assignedAt: string;
  expiresAt?: string;
  isActive: boolean;
  scope?: {
    projects?: string[];
    departments?: string[];
    locations?: string[];
  };
  createdAt: string;
  updatedAt: string;
}

export interface RoleTemplate {
  _id: string;
  name: string;
  displayName: string;
  description: string;
  category: 'construction' | 'management' | 'client' | 'vendor';
  permissions: string[]; // permission IDs
  isPublic: boolean;
  createdBy: {
    _id: string;
    name: string;
  };
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface RoleFilters {
  category?: string;
  isActive?: boolean;
  level?: number;
  search?: string;
  hasUsers?: boolean;
}

export interface RoleStats {
  totalRoles: number;
  activeRoles: number;
  systemRoles: number;
  customRoles: number;
  totalUsers: number;
  usersWithRoles: number;
  mostUsedRoles: {
    roleId: string;
    roleName: string;
    userCount: number;
  }[];
  permissionUsage: {
    permissionId: string;
    permissionName: string;
    roleCount: number;
  }[];
}

// Axios instance with auth token
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Role Management Service
class RoleManagementService {
  // Role Management
  async getRoles(filters?: RoleFilters): Promise<Role[]> {
    const response = await api.get('/roles', { params: filters });
    // Support both { success, data } and array responses
    return Array.isArray(response.data) ? response.data : (response.data?.data || []);
  }

  async getRole(id: string): Promise<Role> {
    const response = await api.get(`/roles/${id}`);
    return response.data?.data || response.data;
  }

  async createRole(role: Omit<Role, '_id' | 'createdAt' | 'updatedAt' | 'users' | 'createdBy'>): Promise<Role> {
    const response = await api.post('/roles', role);
    return response.data;
  }

  async updateRole(id: string, role: Partial<Role>): Promise<Role> {
    const response = await api.put(`/roles/${id}`, role);
    return response.data;
  }

  async deleteRole(id: string): Promise<void> {
    await api.delete(`/roles/${id}`);
  }

  async duplicateRole(id: string, newName: string): Promise<Role> {
    const response = await api.post(`/roles/${id}/duplicate`, { name: newName });
    return response.data;
  }

  // Permission Management
  async getPermissions(): Promise<Permission[]> {
    const response = await api.get('/permissions');
    return response.data;
  }

  async getPermission(id: string): Promise<Permission> {
    const response = await api.get(`/permissions/${id}`);
    return response.data;
  }

  async createPermission(permission: Omit<Permission, '_id' | 'createdAt' | 'updatedAt'>): Promise<Permission> {
    const response = await api.post('/permissions', permission);
    return response.data;
  }

  async updatePermission(id: string, permission: Partial<Permission>): Promise<Permission> {
    const response = await api.put(`/permissions/${id}`, permission);
    return response.data;
  }

  async deletePermission(id: string): Promise<void> {
    await api.delete(`/permissions/${id}`);
  }

  // Role-Permission Management
  async addPermissionToRole(roleId: string, permissionId: string): Promise<Role> {
    const response = await api.post(`/roles/${roleId}/permissions`, { permissionId });
    return response.data;
  }

  async removePermissionFromRole(roleId: string, permissionId: string): Promise<Role> {
    const response = await api.delete(`/roles/${roleId}/permissions/${permissionId}`);
    return response.data;
  }

  async updateRolePermissions(roleId: string, permissionIds: string[]): Promise<Role> {
    const response = await api.put(`/roles/${roleId}/permissions`, { permissionIds });
    return response.data;
  }

  // User-Role Management
  async getUserRoles(userId?: string): Promise<UserRole[]> {
    const params = userId ? { userId } : {};
    const response = await api.get('/user-roles', { params });
    return response.data;
  }

  async assignRoleToUser(userId: string, roleId: string, scope?: UserRole['scope'], expiresAt?: string): Promise<UserRole> {
    const response = await api.post('/user-roles', {
      userId,
      roleId,
      scope,
      expiresAt,
    });
    return response.data;
  }

  async removeRoleFromUser(userRoleId: string): Promise<void> {
    await api.delete(`/user-roles/${userRoleId}`);
  }

  async updateUserRole(userRoleId: string, updates: Partial<UserRole>): Promise<UserRole> {
    const response = await api.put(`/user-roles/${userRoleId}`, updates);
    return response.data;
  }

  async getUserPermissions(userId: string): Promise<Permission[]> {
    const response = await api.get(`/users/${userId}/permissions`);
    return response.data;
  }

  // Role Templates
  async getRoleTemplates(): Promise<RoleTemplate[]> {
    const response = await api.get('/role-templates');
    return response.data;
  }

  async getRoleTemplate(id: string): Promise<RoleTemplate> {
    const response = await api.get(`/role-templates/${id}`);
    return response.data;
  }

  async createRoleTemplate(template: Omit<RoleTemplate, '_id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'usageCount'>): Promise<RoleTemplate> {
    const response = await api.post('/role-templates', template);
    return response.data;
  }

  async createRoleFromTemplate(templateId: string, roleName: string): Promise<Role> {
    const response = await api.post(`/role-templates/${templateId}/create-role`, { name: roleName });
    return response.data;
  }

  // Bulk Operations
  async bulkAssignRoles(userIds: string[], roleId: string): Promise<UserRole[]> {
    const response = await api.post('/user-roles/bulk-assign', { userIds, roleId });
    return response.data;
  }

  async bulkRemoveRoles(userRoleIds: string[]): Promise<void> {
    await api.delete('/user-roles/bulk-remove', { data: { userRoleIds } });
  }

  async bulkUpdateRoleStatus(roleIds: string[], isActive: boolean): Promise<void> {
    await api.patch('/roles/bulk-status', { roleIds, isActive });
  }

  // Role Hierarchy
  async getRoleHierarchy(): Promise<any> {
    const response = await api.get('/roles/hierarchy');
    return response.data;
  }

  async updateRoleHierarchy(roleId: string, parentRoleId?: string): Promise<Role> {
    const response = await api.patch(`/roles/${roleId}/hierarchy`, { parentRoleId });
    return response.data;
  }

  // Statistics and Reports
  async getStats(): Promise<RoleStats> {
    const response = await api.get('/roles/stats');
    return response.data;
  }

  async getRoleUsageReport(roleId: string): Promise<any> {
    const response = await api.get(`/roles/${roleId}/usage-report`);
    return response.data;
  }

  async getPermissionAuditLog(permissionId?: string, userId?: string): Promise<any[]> {
    const params: any = {};
    if (permissionId) params.permissionId = permissionId;
    if (userId) params.userId = userId;
    
    const response = await api.get('/permissions/audit-log', { params });
    return response.data;
  }

  // Validation and Testing
  async validateRolePermissions(roleId: string): Promise<any> {
    const response = await api.post(`/roles/${roleId}/validate`);
    return response.data;
  }

  async testUserPermission(userId: string, resource: string, action: string): Promise<boolean> {
    const response = await api.post('/permissions/test', {
      userId,
      resource,
      action,
    });
    return response.data.allowed;
  }

  // Export
  async exportRoles(format: 'csv' | 'excel' | 'json' = 'csv'): Promise<Blob> {
    const response = await api.get('/roles/export', {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  }

  async exportPermissions(format: 'csv' | 'excel' | 'json' = 'csv'): Promise<Blob> {
    const response = await api.get('/permissions/export', {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  }

  async exportUserRoles(format: 'csv' | 'excel' | 'json' = 'csv'): Promise<Blob> {
    const response = await api.get('/user-roles/export', {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  }
}

export const roleManagementService = new RoleManagementService();
export default roleManagementService;